import { useState, useCallback } from 'react';
import validator from '../utils/validators';

/**
 * Hook personalizado para validación de formularios
 * @param {Object} initialData - Datos iniciales del formulario
 * @param {Object} schema - Esquema de validación
 * @returns {Object} - Objeto con funciones y estado de validación
 */
export const useFormValidation = (initialData = {}, schema = {}) => {
  const [formData, setFormData] = useState(initialData);
  const [errors, setErrors] = useState({});
  const [isValidating, setIsValidating] = useState(false);

  /**
   * Actualiza un campo del formulario y lo valida
   * @param {string} field - Nombre del campo
   * @param {any} value - Nuevo valor
   * @param {Object} rules - Reglas de validación específicas (opcional)
   */
  const updateField = useCallback((field, value, rules = null) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Validar el campo si hay reglas
    if (rules || schema[field]) {
      const fieldRules = rules || schema[field];
      const isValid = validator.validateField(field, value, fieldRules);
      
      setErrors(prev => ({
        ...prev,
        [field]: isValid ? null : validator.getErrors()[field]
      }));
    }
  }, [schema]);

  /**
   * Valida todo el formulario
   * @returns {boolean} - Si el formulario es válido
   */
  const validateForm = useCallback(() => {
    setIsValidating(true);
    const isValid = validator.validateForm(formData, schema);
    setErrors(validator.getErrors());
    setIsValidating(false);
    return isValid;
  }, [formData, schema]);

  /**
   * Valida un campo específico
   * @param {string} field - Nombre del campo
   * @param {Object} rules - Reglas de validación (opcional)
   * @returns {boolean} - Si el campo es válido
   */
  const validateField = useCallback((field, rules = null) => {
    const fieldRules = rules || schema[field];
    if (!fieldRules) return true;

    const isValid = validator.validateField(field, formData[field], fieldRules);
    setErrors(prev => ({
      ...prev,
      [field]: isValid ? null : validator.getErrors()[field]
    }));
    return isValid;
  }, [formData, schema]);

  /**
   * Valida contraseñas coincidentes
   * @param {string} passwordField - Campo de contraseña
   * @param {string} confirmField - Campo de confirmación
   * @returns {boolean} - Si las contraseñas coinciden
   */
  const validatePasswordMatch = useCallback((passwordField, confirmField) => {
    const isValid = validator.validatePasswordMatch(
      formData[passwordField], 
      formData[confirmField], 
      confirmField
    );
    setErrors(prev => ({
      ...prev,
      [confirmField]: isValid ? null : validator.getErrors()[confirmField]
    }));
    return isValid;
  }, [formData]);

  /**
   * Limpia todos los errores
   */
  const clearErrors = useCallback(() => {
    setErrors({});
    validator.clearErrors();
  }, []);

  /**
   * Resetea el formulario a los datos iniciales
   */
  const resetForm = useCallback(() => {
    setFormData(initialData);
    setErrors({});
    validator.clearErrors();
  }, [initialData]);

  /**
   * Obtiene los datos sanitizados
   * @returns {Object} - Datos sanitizados
   */
  const getSanitizedData = useCallback(() => {
    return validator.getSanitizedData();
  }, []);

  /**
   * Verifica si hay errores
   * @returns {boolean} - Si hay errores
   */
  const hasErrors = useCallback(() => {
    return Object.values(errors).some(error => error !== null);
  }, [errors]);

  /**
   * Obtiene el error de un campo específico
   * @param {string} field - Nombre del campo
   * @returns {string|null} - Error del campo o null
   */
  const getFieldError = useCallback((field) => {
    return errors[field] || null;
  }, [errors]);

  /**
   * Verifica si un campo tiene error
   * @param {string} field - Nombre del campo
   * @returns {boolean} - Si el campo tiene error
   */
  const hasFieldError = useCallback((field) => {
    return Boolean(errors[field]);
  }, [errors]);

  return {
    formData,
    setFormData,
    errors,
    isValidating,
    updateField,
    validateForm,
    validateField,
    validatePasswordMatch,
    clearErrors,
    resetForm,
    getSanitizedData,
    hasErrors,
    getFieldError,
    hasFieldError
  };
};

export default useFormValidation;